import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface ParallaxHeroProps {
  imageUrl: string | null;
  barName: string;
}

// Generic bar placeholder - a gradient with a subtle pattern
const PLACEHOLDER_GRADIENT = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)';

export function ParallaxHero({ imageUrl, barName }: ParallaxHeroProps) {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // On desktop, content scrolls in a container, not the window
    const scrollContainer = document.getElementById('main-scroll-container');

    const handleScroll = () => {
      if (scrollContainer) {
        setScrollY(scrollContainer.scrollTop);
      } else {
        setScrollY(window.scrollY);
      }
    };

    // Listen to both in case layout changes
    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollContainer?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Parallax: start at center, image moves down as user scrolls down
  // This reveals more of the top of the image as you scroll
  const parallaxShift = Math.min(scrollY * 0.05, 20); // max 20% shift
  const objectPositionY = 50 - parallaxShift; // 50% -> 30% (center toward top)

  // Get initials for placeholder
  const initials = barName
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="relative h-48 sm:h-56 md:h-72 overflow-hidden -mx-4 sm:-mx-6 bg-[#1a1a2e]"
      style={{ marginTop: '-1.5rem' }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={barName}
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
          style={{
            objectPosition: `center ${objectPositionY}%`,
          }}
        />
      ) : (
        <div
          className="absolute inset-0 w-full h-full flex items-center justify-center"
          style={{
            background: PLACEHOLDER_GRADIENT,
          }}
        >
          <span className="text-6xl md:text-7xl font-bold text-white/20">{initials}</span>
        </div>
      )}

      {/* Gradient overlay for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-bg)] via-transparent to-transparent opacity-60" />

      {/* Back button overlay */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-2 py-1 text-sm text-white bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </div>
  );
}
